import IStep from '../../lib/IStep';
import EventsManager from '../../services/EventsManager';
import CategoryKeywordModel from '../CategoryKeywords/models/CategoryKeywordModel';
import DesignModel from '../Design/models/DesignModel';
import FinalizeSitemapModel from '../FinalizeSitemap/models/FinalizeSitemapModel';
import BusinessModel from '../FindYourBusiness/models/BusinessModel';
import SiteMapModel from '../Sitemap/models/SiteMapModel';
import EditorStateMachine from './model/EditorStateMachine';
import PageGeneratedData from './model/generated-data/PageGeneratedData';
import TemplateGeneratedData from './model/generated-data/TemplateGeneratedData';
import SiteBuilderModel from './model/SiteBuilderModel';
import { CHANGE_PAGE_EVENT } from './step-events';

export default class Step implements IStep {
  /**
   * Fired when the step is initialized
   */
  onInit() {
    EventsManager.on(CHANGE_PAGE_EVENT, () => {
      // if the user changed the template while the text was regenerating
      EditorStateMachine.transition('onElementFinishRegeneratingData', {});
    });
  }

  public async prepareStepData() {
    const colorScheme = DesignModel.getFinalColorScheme;
    const mainFont = DesignModel.selectedFontData?.mainFontFamily;
    const displayFont = DesignModel.selectedFontData?.displayFontFamily;

    const categoryName = BusinessModel.getWizardData().category;
    const categoryData = CategoryKeywordModel.getOutputCategoryData();

    await SiteMapModel.fetchSiteMap({
      category: categoryName,
      ...categoryData,
      languageCode: 'de'
    });

    let sitemapNodes = SiteMapModel.getSiteMapNodesForSessionStorage();

    SiteMapModel.siteMapWizardData.setFormData({
      siteMap: sitemapNodes.siteMap
    });

    let dynamicPages = SiteMapModel.getDynamicPages();
    if (dynamicPages.length > 1) {
      // get the first four dynamic pages
      dynamicPages = dynamicPages.slice(0, 4);
      dynamicPages.forEach((page) => {
        FinalizeSitemapModel.addImportantPage({
          id: page.id,
          pageName: page.data.pageName
        });
      });

      FinalizeSitemapModel.finalizeSitemapWizardData.setFormData({
        important_pages: dynamicPages.map((page) => page.id.toString())
      });
    }

    // load all data needed for the preview step
    const resData: any = await Promise.all([
      SiteBuilderModel.loadTemplateData(),
      SiteBuilderModel.loadCssThemeConfig(colorScheme, {
        main: {
          name: mainFont
        },
        display: {
          name: displayFont
        }
      })
    ]);

    SiteBuilderModel.setFinalColorScheme(colorScheme);

    // get the tree data from step SiteMap
    const siteMapTreeData = SiteMapModel.getAllInternalPageNodes();

    // store the tree link list for later use
    SiteBuilderModel.setPageTreeLinkList(siteMapTreeData.treeLinkList);

    // extract the sitemap data from the response
    const sitemap = resData[0].sitemap;

    SiteBuilderModel.setGeneratedPages(
      siteMapTreeData.tree
        .map((node) => {
          let generatedData = sitemap.find((item) => item.id === node.id);

          if (!generatedData) {
            return null;
          }

          return new PageGeneratedData({
            id: node.id,
            parent: node.parent,
            type: node.data.type,
            textAreas: generatedData.areas,
            pictureAreas: generatedData.pictureAreas,
            pageName: node.data.pageName,
            pagePath: node.data.path,
            isCustomPage: node.data.isCustomPage,
            isPageExternal: node.data.isPageExternal
          });
        })
        .filter((node) => node !== null)
    );

    const firstShownInPreviewPageId = siteMapTreeData.treeLinkList
      .next()
      .getId();

    const firstShownInPreviewPage = SiteBuilderModel.generatedPages.find(
      (node) => node.getId() === firstShownInPreviewPageId
    );

    const templateData = new TemplateGeneratedData({
      templateAreas: resData[0].templateAreas,
      templateImages: resData[0].templateImages,
      templateStyleId: resData[0].templateStyleId,
      websiteTemplate: resData[0].websiteTemplate
    });

    SiteBuilderModel.setCurrentShownInPreviewGeneratedPage(
      firstShownInPreviewPage
    );
    SiteBuilderModel.setTemplateGeneratedData(templateData);
  }
}
